/**
 * ItemRelationshipFlags — the relationship flags an item carries wherever it is
 * shown in a ranked or decided list (canon 12 Rule 17: the flag travels with
 * the item). Renders nothing for an item with no relationship worth telling.
 *
 * Reads the workspace relationship graph from the store and loads it once if
 * the page has not.
 */

import { useMemo } from 'react';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { getRelationshipGuideEntry, relationshipChipClass } from '../../lib/relationshipGuide';
import { describeItemRelationshipFlags, relationshipFlagSentence } from '../../lib/itemRelationshipFlags';
import { useRelationshipGraph } from '../../hooks/prioritization/useRelationshipGraph';

interface ItemRelationshipFlagsProps {
  itemId: string | null | undefined;
  className?: string;
}

export function ItemRelationshipFlags({ itemId, className }: ItemRelationshipFlagsProps) {
  const graph = useRelationshipGraph();
  const items = useItemsStore((s) => s.items);

  const flags = useMemo(() => (itemId ? describeItemRelationshipFlags(itemId, graph) : []), [itemId, graph]);
  if (flags.length === 0) return null;

  const titleOf = (id: string) => items.find((i) => i.id === id)?.title ?? 'an item you cannot see';

  return (
    <span className={`inline-flex flex-wrap items-center gap-1 align-middle ${className ?? ''}`} data-testid="item-relationship-flags">
      {flags.map((flag) => {
        const Icon = getRelationshipGuideEntry(flag.guideId)?.Icon;
        const sentence = relationshipFlagSentence(flag, titleOf);
        return (
          <span
            key={flag.kind}
            title={sentence}
            aria-label={sentence}
            className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-xs font-medium leading-none whitespace-nowrap ${relationshipChipClass(flag.guideId)}`}
          >
            {Icon && <Icon className="h-2.5 w-2.5 shrink-0" />}
            {flag.label}
          </span>
        );
      })}
    </span>
  );
}
